/**
 * SdkError runtime class — single failure type the SDK ever surfaces.
 *
 * Pins (file 05):
 *   §L2645 · 1 · Error taxonomy · seven categories, one shape
 *   §L2664 · 2 · The v2 SdkError shape
 *   §L2709 · 3 · Per-strategy error semantics · what query.error means
 *   §L2726 · 4 · React integration · boundaries, suspense, toasts
 *   §L2779 · 5 · Mutation error UX · the four canonical patterns
 *   §L2800 · 6 · Degraded mode · NATS down, REST up
 *   §L2814 · 7 · Schema validation · per-strategy policy
 *   §L2827 · 8 · Telemetry · one event bus, three subscribers
 *   §L2845 · 9 · Worked examples · what the user actually sees
 *   §L2884 · The bottom line
 *   §L2704 · `correlationId` is mandatory; `cause` chain preserved;
 *            no `retryable: boolean` (policy lives in the retry layer)
 *
 * The runtime class implements the discriminated-union TYPES from
 * `../types/SdkError.ts` (T108): 8 categories, per-category code unions,
 * an `SdkErrorBase` shape. Categories: network, auth, validation, server,
 * realtime-degraded, schema, rate-limited, precondition_failed.
 *
 * Class invariants:
 *   - `name === 'SdkError'`           — isSdkError() structural check
 *   - `instanceof SdkError === true`  — class-based check
 *   - `instanceof Error    === true`  — boundaries / Promise rejection paths
 *   - `correlationId`                 — mandatory, non-empty
 *   - `emittedAt`                     — auto-filled performance.now()
 *   - `cause`                         — preserved via native Error.cause
 *   - JSON.stringify round-trip       — all enumerable fields survive
 */

import type {
  SdkErrorBase,
  SdkErrorCategory,
  SdkError as SdkErrorShape,
} from '../types/SdkError.js';
import { resolveCorrelationId } from './correlation.js';

/** Frozen list of every category the v2 SDK can produce. */
export const SDK_ERROR_CATEGORIES: readonly SdkErrorCategory[] = Object.freeze([
  'network',
  'auth',
  'validation',
  'server',
  'realtime-degraded',
  'schema',
  'rate-limited',
  'precondition_failed',
] as const) as readonly SdkErrorCategory[];

/**
 * Spec-anchor record (file 05). Frozen so a downstream PR cannot silently
 * mutate the pin set. Tests assert this object is present + frozen.
 */
export const ERROR_TAXONOMY_SPEC_LINES = Object.freeze({
  taxonomy: 2645,
  shape: 2664,
  perStrategySemantics: 2709,
  reactIntegration: 2726,
  mutationUx: 2779,
  degradedMode: 2800,
  schemaValidation: 2814,
  telemetry: 2827,
  workedExamples: 2845,
  bottomLine: 2884,
  correlationIdMandatory: 2704,
} as const);

export type SdkErrorInit = Omit<SdkErrorBase, 'name' | 'emittedAt'> & Record<string, unknown>;

/**
 * Runtime SdkError. Implements `SdkErrorBase` from T108 so the
 * discriminated-union types in `../types/SdkError.ts` still narrow.
 *
 * Note: T108's `SdkErrorBase` declares `name` as the literal `'SdkError'`
 * and `correlationId` as required. The class auto-fills `name`, `emittedAt`,
 * and assigns every caller-supplied field via Object.assign so the
 * resulting instance is structurally identical to the union variant.
 */
export class SdkError extends Error implements SdkErrorBase {
  override readonly name = 'SdkError' as const;
  readonly category: SdkErrorCategory;
  readonly code: string;
  readonly userMessage: string;
  readonly devMessage: string;
  readonly correlationId: string;
  readonly emittedAt: number;
  // Per-variant extras (httpStatus, fieldErrors, subjects, retryAfterMs,
  // subject, path) land via Object.assign — they are typed by the T108
  // union variants. An index signature is needed so SdkError satisfies
  // the SdkErrorShape variants that carry these extras after the
  // `as unknown as SdkError` cast in `makeSdkError`.
  readonly [k: string]: unknown;

  constructor(init: SdkErrorInit) {
    super(init.devMessage, init.cause !== undefined ? { cause: init.cause } : undefined);
    // Object.assign copies all caller fields (incl. variant-specific extras).
    Object.assign(this, init);
    this.category = init.category;
    this.code = init.code;
    this.userMessage = init.userMessage;
    this.devMessage = init.devMessage;
    this.correlationId =
      typeof init.correlationId === 'string' && init.correlationId.length > 0
        ? init.correlationId
        : resolveCorrelationId(undefined);
    this.emittedAt = performance.now();
    // Make `name` enumerable so JSON.stringify includes it — Error sets
    // name on the prototype, not the instance.
    Object.defineProperty(this, 'name', {
      value: 'SdkError',
      enumerable: true,
      writable: false,
      configurable: false,
    });
  }
}

/**
 * Factory matching the discriminated-union return type from T108. Prefer
 * this in code that needs the narrowed union (e.g. `if (e.category ===
 * 'rate-limited') e.retryAfterMs` typing through).
 */
export function makeSdkError(init: SdkErrorInit): SdkErrorShape {
  return new SdkError(init) as unknown as SdkErrorShape;
}

/** Back-compat alias requested by T9001's public surface. */
export const SdkErrorImpl = SdkError;
export type SdkErrorImpl = SdkError;
